import React from "react";
import { Badge, Code, CodeBlock, Note, Section, Table, Tbody, Thead, MONO, TD } from "./primitives";

const RESPONSE_SHAPE = `{
  "meta": { "total_rows": null, "pagination": { "total": 0, "page": 0, "page_size": 0, "next": null, "prev": null } },
  "request": { "method": "POST", "path": "https://.../metadata/file-center/upload/324/logs/2025/09/26/report.pdf" },
  "response": {
    "code": 200,
    "data": [
      { "blob_url": "https://<storage-account>.blob.core.windows.net/<container>/324/logs/2025/09/26/report.pdf?<sas-token>" }
    ]
  },
  "error": null,
  "warnings": null
}`;

const FULL_EXAMPLE = `import requests

# Assumes \`headers\` and \`base_url\` from the Authentication section.

# --- Step 1: request a pre-signed upload URL -----------------------------
asset_id = 324
file_type = "logs"
year, month, day = "2025", "9", "26"   # the service zero-pads single digits
file_name = "report.pdf"

upload_request_url = (
    f"{base_url}metadata/file-center/upload/"
    f"{asset_id}/{file_type}/{year}/{month}/{day}/{file_name}"
)
resp = requests.post(upload_request_url, headers=headers)
resp.raise_for_status()
blob_url = resp.json()["response"]["data"][0]["blob_url"]

# --- Step 2: upload the bytes straight to Azure Blob Storage -------------
with open("/path/to/report.pdf", "rb") as file_obj:
    upload_response = requests.put(
        blob_url,
        data=file_obj,
        headers={"x-ms-blob-type": "BlockBlob"},
    )
upload_response.raise_for_status()
print("Upload complete")`;

const FileUploadDoc: React.FC = () => (
  <div className="space-y-8">
    <p className="text-sm leading-6 text-gray-700">
      File Center uses short-lived Azure Blob Storage SAS URLs for uploads and downloads. For
      uploads, the service issues a write-only URL and the client sends file bytes directly to Azure.
      For downloads, the service returns a read-only URL for the requested file. File bytes never
      pass through the API gateway.
    </p>

    <Section title="Upload endpoint">
      <Table minWidth={780}>
        <Thead cols={["Method", "Path", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} ${MONO} break-all`}>
              /metadata/file-center/upload/{'{asset_id}'}/{'{file_type}'}/{'{year}'}/{'{month}'}/
              {'{date}'}/{'{file_name}'}
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
      <Table minWidth={640}>
        <Thead cols={["Parameter", "Type", "Description"]} />
        <Tbody>
          <tr>
            <td className={`${TD} ${MONO}`}>asset_id</td>
            <td className={TD}>integer</td>
            <td className={TD}>BETM asset ID that owns the file.</td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>file_type</td>
            <td className={TD}>string</td>
            <td className={TD}>
              Logical folder or category, e.g. <Code>logs</Code>, <Code>reports</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>year</td>
            <td className={TD}>string</td>
            <td className={TD}>
              Year, e.g. <Code>2025</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>month</td>
            <td className={TD}>string</td>
            <td className={TD}>
              Month. The service zero-pads single digits, so <Code>9</Code> and <Code>09</Code> both
              work.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>date</td>
            <td className={TD}>string</td>
            <td className={TD}>Day of month; also zero-padded by the service.</td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>file_name</td>
            <td className={TD}>string</td>
            <td className={TD}>Target file name, including the extension.</td>
          </tr>
        </Tbody>
      </Table>
      <p className="text-sm leading-6 text-gray-700">
        The parameters form the blob path{" "}
        <Code>
          {'{asset_id}'}/{'{file_type}'}/{'{year}'}/{'{month}'}/{'{date}'}/{'{file_name}'}
        </Code>
        . The download endpoint names the same segment <Code>{'{day}'}</Code> rather than{" "}
        <Code>{'{date}'}</Code>; the position and meaning are identical.
      </p>
    </Section>

    <Section title="Two-step upload flow">
      <ol className="list-decimal space-y-2 pl-5 text-sm leading-6 text-gray-700">
        <li>
          <b>Request a pre-signed upload URL.</b> The service validates your entitlement for the
          asset, returns a write-only SAS URL valid for <b>15 minutes</b>, and triggers an
          asynchronous File Center cache refresh so the file appears in <Code>list</Code> calls
          without a manual refresh.
        </li>
        <li>
          <b>Upload the bytes to Azure Blob Storage</b> with an HTTP <Code>PUT</Code> to the returned{" "}
          <Code>blob_url</Code>, including the header{" "}
          <Code>x-ms-blob-type: BlockBlob</Code>.
        </li>
      </ol>
      <CodeBlock code={FULL_EXAMPLE} label="Python - request a URL and upload a file" />
    </Section>

    <Section title="Related endpoints">
      <Table minWidth={800}>
        <Thead cols={["Method", "Path", "Description", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} ${MONO} break-all`}>
              /metadata/file-center/list/{'{asset_id}'}
              ?file_type=&amp;year=&amp;month=&amp;day=&amp;top_count=
            </td>
            <td className={TD}>
              Files for one asset, filtered by any combination of type and date, with folder
              permissions applied.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} ${MONO}`}>/metadata/file-center/list/all</td>
            <td className={TD}>
              Files across multiple assets in one call. Body: <Code>asset_id_list</Code>, with
              optional <Code>file_type</Code>, <Code>year</Code>, <Code>month</Code>,{" "}
              <Code>day</Code>, <Code>top_count</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Download endpoint">
      <Table minWidth={780}>
        <Thead cols={["Method", "Path", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} ${MONO} break-all`}>
              /metadata/file-center/download/{'{asset_id}'}/{'{file_type}'}/{'{year}'}/{'{month}'}/
              {'{day}'}/{'{file_name}'}
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
      <Table minWidth={640}>
        <Thead cols={["Parameter", "Type", "Description"]} />
        <Tbody>
          <tr>
            <td className={`${TD} ${MONO}`}>asset_id</td>
            <td className={TD}>integer</td>
            <td className={TD}>BETM asset ID that owns the file.</td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>file_type</td>
            <td className={TD}>string</td>
            <td className={TD}>Logical folder or category for the file.</td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>year / month / day</td>
            <td className={TD}>string</td>
            <td className={TD}>Date path for the requested file.</td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>file_name</td>
            <td className={TD}>string</td>
            <td className={TD}>File name, including the extension.</td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Two-step download flow">
      <ol className="list-decimal space-y-2 pl-5 text-sm leading-6 text-gray-700">
        <li>
          <b>Request a pre-signed download URL.</b> Call the file-specific endpoint with the asset
          ID, file type, date path, and file name. The service validates access and returns a
          read-only <Code>blob_url</Code> valid for <b>15 minutes</b>.
        </li>
        <li>
          <b>Download the file bytes from Azure Blob Storage.</b> Fetch the returned SAS URL, create
          a browser object URL from the file bytes, and start the download with the original file
          name.
        </li>
      </ol>
      <Note tone="info">
        The File Center UI follows this same sequence. The SAS URL is used only to retrieve the file
        bytes; it is not the final browser download URL.
      </Note>
    </Section>

    <Section title="Response">
      <CodeBlock code={RESPONSE_SHAPE} label="Upload endpoint response" />
    </Section>

    <Section title="Limits and safety">
      <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
        <li>
          <b>URLs expire after 15 minutes</b>, both upload and download. Request a fresh one if the
          transfer does not finish in time.
        </li>
        <li>
          <b>One URL per file.</b> Each URL is scoped to the exact blob path built from the path
          parameters.
        </li>
        <li>
          <b>Uploads return <Code>403</Code></b> if you are not entitled to the asset. The check runs
          before any URL is issued.
        </li>
      </ul>
      <Note>
        <b>Re-using a path overwrites the previous file silently.</b> Uploading to the same{" "}
        <Code>asset_id</Code>/<Code>file_type</Code>/<Code>year</Code>/<Code>month</Code>/
        <Code>date</Code>/<Code>file_name</Code> replaces the existing blob in place. The service
        does not check whether a blob is already there, and the <Code>PUT</Code> carries no
        conditional header to prevent it - it is a standard Azure <Code>PUT Blob</Code>, which
        creates or overwrites. You get a normal success either way: no conflict error, no versioning,
        and no way to recover the previous content through the API. Use a distinct file name, date or
        type segment for each upload.
      </Note>
    </Section>
  </div>
);

export default FileUploadDoc;
